import { useCallback, useEffect, useMemo, useState } from "react"

import { getPurchaseOrderSearchInitialValue } from "../utils"

import { useGetPurchaseOrderSuggestions } from "./useGetPurchaseOrderSuggestions"

import type { Order } from "@/graphql/codegen/graphql"
import type { AutocompleteOption } from "@/types/form-fields"

interface usePurchaseOrderSelectionDialogReturn {
  searchQuery: string | AutocompleteOption
  inputValue: string
  selectedPurchaseOrders: Order[]
  purchaseOrders: Order[]
  isFetching: boolean
  handleSearch: (searchedItem: string | AutocompleteOption) => void
  handleInputChange: (value: string) => void
  handleSelect: (purchaseOrders: Order[]) => void
  reset: () => void
}

export const usePurchaseOrderSelectionDialog = (excludeOrderIds?: number[]): usePurchaseOrderSelectionDialogReturn => {
  const [searchQuery, setSearchQuery] = useState<string | AutocompleteOption>("")
  const [inputValue, setInputValue] = useState("")
  const [selectedPurchaseOrders, setSelectedPurchaseOrders] = useState<Order[]>([])

  const searchString = useMemo(() => {
    if (typeof searchQuery === "string") {
      return searchQuery
    }
    if (searchQuery?.id) {
      return String(searchQuery.id)
    }
    // Fallback: clean the label by removing "PO# " prefix
    const label = searchQuery?.label || ""
    return label.startsWith("PO# ") ? label.replace("PO# ", "") : label
  }, [searchQuery])

  const { purchaseOrders, isFetching } = useGetPurchaseOrderSuggestions({
    query: searchString,
    excludeOrderIds,
  })

  const handleSearch = useCallback(
    (searchedItem: string | AutocompleteOption) => {
      const actualSearchItem = searchedItem || inputValue
      if (!actualSearchItem) return

      setSearchQuery(actualSearchItem)

      const displayValue = getPurchaseOrderSearchInitialValue(actualSearchItem)
      setInputValue(displayValue)
    },
    [inputValue]
  )

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  const handleSelect = useCallback((purchaseOrders: Order[]) => {
    setSelectedPurchaseOrders(purchaseOrders)
  }, [])

  const reset = useCallback(() => {
    setSearchQuery("")
    setInputValue("")
    setSelectedPurchaseOrders([])
  }, [])

  // Reset state when component unmounts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => reset(), [])

  return {
    searchQuery,
    inputValue,
    selectedPurchaseOrders,
    purchaseOrders: purchaseOrders || [],
    isFetching,
    handleSearch,
    handleInputChange,
    handleSelect,
    reset,
  }
}
